import axios, { AxiosResponse } from 'axios';
import { addSeconds } from 'date-fns';
import { ApiClient } from '../ApiClient';
import { AMAGI_NOW_DATA_OBJECT_STORE, USER_SIGNED_OUT_KEY } from '../constants';
import { REFRESH_MARGIN } from '../constants/numbers';
import { ILoginData } from '../types/loginData';
import {
  openIndexDB,
  setDataInIDB,
  setRefreshTokenTimerCookie,
} from '../utils';
import { gotoSignedInApp, gotoSignInPage } from '../utils/urls';

const AuthClient = axios.create({ withCredentials: true });

const defaultErrorMessage = {
  response: {
    data: {
      error: 'Something happened. Please try again later',
    },
  },
};

AuthClient.interceptors.request.use(
  (config) => config,
  () => {
    return Promise.reject(defaultErrorMessage);
  },
);

AuthClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.data &&
      (error.response.data.error || error.response.data.errors)
    ) {
      return Promise.reject(error);
    }
    return Promise.reject(defaultErrorMessage);
  },
);

export function generateTokenApi(
  baseURL: string,
): Promise<AxiosResponse<{ expires_in: number }>> {
  return AuthClient.post('/accounts/users/generate/token', null, { baseURL });
}

export function validateUser(): Promise<AxiosResponse<ILoginData>> {
  return ApiClient.post('/accounts/users/validate');
}

export const signIn = (
  baseURL: string,
  email_id: string,
  password: string,
  domain?: string,
): Promise<AxiosResponse<ILoginData>> => {
  return AuthClient.post(
    `/accounts/users/signin`,
    {
      email_id,
      password,
      domain,
    },
    { baseURL },
  ).then((res) => {
    setRefreshTokenTimerCookie(
      addSeconds(new Date(), res.data.expires_in),
      addSeconds(new Date(), res.data.expires_in + REFRESH_MARGIN),
      res.data.expires_in,
    );
    openIndexDB()
      .then((db) => {
        setDataInIDB(
          USER_SIGNED_OUT_KEY,
          false,
          AMAGI_NOW_DATA_OBJECT_STORE,
          db,
        )
          .then(() => {
            gotoSignedInApp(res.data);
          })
          .catch((err) =>
            console.error(
              'Something happened while setting false to USER_SIGNED_OUT_KEY',
              err,
            ),
          );
      })
      .catch((err) =>
        console.error('Something happened while opening index db', err),
      );
    return res;
  });
};

export function signOut(baseURL: string, domain: string): Promise<void> {
  return ApiClient.post(`/accounts/${domain}/users/signout`, null, {
    baseURL,
  }).then(() => {
    openIndexDB()
      .then((db) => {
        setDataInIDB(USER_SIGNED_OUT_KEY, true, AMAGI_NOW_DATA_OBJECT_STORE, db)
          .then(() => {
            gotoSignInPage({
              shouldSavePreviousRoute: false,
            });
          })
          .catch((err) =>
            console.error(
              'Something happened while setting false to USER_SIGNED_OUT_KEY',
              err,
            ),
          );
      })
      .catch((err) =>
        console.error('Something happened while opening index db', err),
      );
  });
}

export function triggerForgotPassword(
  baseURL: string,
  email: string,
  domain: string,
): Promise<void> {
  return AuthClient.post(
    '/accounts/users/password/forgot',
    { email_id: email, domain: domain },
    { baseURL },
  );
}

export const forgotPasswordConfirm = (
  baseURL: string,
  email_id: string,
  verification_code: string,
  password: string,
  domain: string,
): Promise<AxiosResponse<IDefaultResponse>> => {
  const params = { email_id, verification_code, password, domain };
  return AuthClient.post('/accounts/users/password/forgot/confirm', null, {
    params,
    baseURL,
  });
};

export interface IDefaultResponse {
  message: string;
}
